import axios from 'axios';
import Cookies from 'universal-cookie';
import get from 'lodash.get';

import { SIGNIN_UNSUCCESSFUL_LANGUAGE, SIGNIN_LOCKED_LANGUAGE, SEARCH_HISTORY_STORAGE_KEY } from 'components/utils/constants';
import { getAxiosError, loginCookieSettings, getLocalStorageItem } from 'components/utils/helpers';
import { setUserFilters, setFilters } from './search';
import { CLEAR_STORE } from '../reducers';
import { fetchHomepage } from './homepage';
import {
  SET_USER_LOADING,
  SET_USER_ERROR,
  SET_USER,
  RESET_USER,
  SET_ACCOUNT_UNLOCKED,
  SET_ATTEMPTED_AUTO_SIGNIN,
  UPDATE_USER_PERMISSIONS,
  RESET_USER_ERROR,
  SET_USER_PREFERENCES,
} from '../reducers/user';
import { setLayout, closeRestrictions } from './globalActions';

const SIGNIN_TIMEOUT = process.env.SIGNIN_TIMEOUT || 10000;

export const clearStore = () => ({
  type: CLEAR_STORE,
});

export const setUserLoading = () => ({
  type: SET_USER_LOADING,
});

export const setAccountUnlocked = (error = '') => ({
  type: SET_ACCOUNT_UNLOCKED,
  data: {
    error,
    loading: false,
    accountUnlocked: true,
  },
});

export const setUnlockKey = (error = '') => ({
  type: SET_ACCOUNT_UNLOCKED,
  data: {
    error,
    loading: false,
    unlockKeyGenerated: true,
  },
});

export const setUser = user => ({
  type: SET_USER,
  data: { user },
});

export const resetUser = () => ({
  type: RESET_USER,
});

// Does not mean user is actually singed-in (just that auto sign-in request finished)
export const setAttemptedAutoSignIn = attemptedAutoSignin => ({
  type: SET_ATTEMPTED_AUTO_SIGNIN,
  data: { attemptedAutoSignin },
});

export const setUserError = error => ({
  type: SET_USER_ERROR,
  data: { error },
});

export const updateUserPermissions = permissions => ({
  type: UPDATE_USER_PERMISSIONS,
  data: { permissions },
});

export const resetUserError = () => ({
  type: RESET_USER_ERROR,
});

export const setPreferences = preferences => ({
  type: SET_USER_PREFERENCES,
  data: { preferences },
});

const userFilterReducer = (userFilter = []) => userFilter.reduce((result, { id }) => {
  /* eslint  no-param-reassign: 0 */ // proper param re-assignment in this case
  result[id] = true;
  return result;
}, {});

export const loadStorageFilters = (searchHistory, data) => async (dispatch) => {
  // todo - remove this cookie-removal code once the original searchHistory cookie TTL expires (~1/6/2020)
  const cookies = new Cookies();
  cookies.remove('searchHistory');

  const filters = get(searchHistory, 'filters');
  if (filters) {
    dispatch(setFilters(filters));
    dispatch(setUserFilters(filters));
  } else {
    const { feeds, languages } = data || {};
    const userFilters = { feeds: userFilterReducer(feeds), languages: userFilterReducer(languages) };
    dispatch(setUserFilters(userFilters));
  }
};

export const signinUser = (email, password) => async (dispatch, getState) => {
  dispatch(setUserLoading());
  const cookies = new Cookies();
  const searchHistory = getLocalStorageItem(SEARCH_HISTORY_STORAGE_KEY);

  try {
    const { data } = await axios.post('/api/signin', { email, password }, { timeout: SIGNIN_TIMEOUT });

    cookies.set('login', data.token, loginCookieSettings);
    delete data.token;

    dispatch(loadStorageFilters(searchHistory, data));
    dispatch(setUser(data));
  } catch (err) {
    const { status, message } = getAxiosError(err);
    let signinError;

    if (status === 401) {
      signinError = SIGNIN_UNSUCCESSFUL_LANGUAGE;
    } else if (status === 423) {
      signinError = SIGNIN_LOCKED_LANGUAGE;

      const { signin: { showSignin } } = getState().globalActions;
      if (showSignin) {
        dispatch(closeRestrictions());
      }
    } else {
      const errorMsg = typeof message === 'object' ? message.error : message;
      signinError = `Error: ${errorMsg}`;
    }

    dispatch(setUserError(signinError));
  }
  dispatch(setAttemptedAutoSignIn(true));
  dispatch(setLayout(searchHistory.layout));
};

export const autoSigninUser = () => async (dispatch) => {
  const cookies = new Cookies();
  const loginCookie = cookies.get('login');
  const searchHistory = getLocalStorageItem(SEARCH_HISTORY_STORAGE_KEY);
  let userData = null;
  try {
    if (loginCookie) {
      const { data } = await axios.post('/api/auth', {}, {
        timeout: SIGNIN_TIMEOUT,
      });
      dispatch(setUser(data));
      userData = data;
    }
  } catch (err) {
    const { status, message } = getAxiosError(err);
    if (status !== 401) {
      console.error(err);
      dispatch(setUserError(`Error: ${message}`));
    }
  }
  dispatch(loadStorageFilters(searchHistory, userData));
  dispatch(setAttemptedAutoSignIn(true));
  dispatch(setLayout(searchHistory.layout));
};

export const logoutUser = () => async (dispatch) => {
  const cookies = new Cookies();
  cookies.remove('login');
  dispatch(setUser({}));
  dispatch(clearStore());
  dispatch(fetchHomepage());
  await dispatch(setAttemptedAutoSignIn(true));
};

export const updateUserNewsPermissions = (id, storyPermissions) => (dispatch, getState) => {
  const { user: { permissions: currentPermisions = {} } } = getState().user;
  const permissions = {
    ...currentPermisions,
    news: {
      ...currentPermisions.news,
      [id]: storyPermissions,
    },
  };
  dispatch(updateUserPermissions(permissions));
};

export const hasPermission = permission => (_, getState) => {
  if (!permission) {
    return false;
  }

  const { user: { permissions } } = getState().user;
  return get(permissions, permission, false);
};

export const generateUnlockKey = email => async (dispatch) => {
  dispatch(setUserLoading());
  try {
    await axios.post('/api/users/unlock/key', { email }, { timeout: SIGNIN_TIMEOUT });
    dispatch(setUnlockKey());
  } catch (err) {
    const { message } = getAxiosError(err);
    dispatch(setUserError(message));
  }
};

export const verifyUnlockKey = (id, key) => async (dispatch) => {
  dispatch(setUserLoading());
  try {
    await axios.post('/api/users/unlock', { id, key }, { timeout: SIGNIN_TIMEOUT });
    dispatch(setAccountUnlocked());
  } catch (err) {
    const { message } = getAxiosError(err);
    dispatch(setUserError(message));
  }
};

export const updatePreferences = userPrefs => async (dispatch, getState) => {
  const { user: { preferences } } = getState().user;
  const updatedPrefs = { ...preferences, ...userPrefs };
  dispatch(setUserLoading());
  try {
    await axios.put('/api/users/preferences', { preferences: updatedPrefs }, { timeout: SIGNIN_TIMEOUT });
    dispatch(setPreferences(updatedPrefs));
  } catch (err) {
    const { message } = getAxiosError(err);
    dispatch(setUserError(message));
  }
};
